import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Deploy } from "@/types";
import { useMemo } from "react";
import { BuildLogsList } from "./BuildLogsList";
import { Terminal } from "lucide-react";

interface BuildLogsViewerProps {
  deploy: Deploy;
}

// Terminal Window Styling
const panelClass =
  "rounded-lg border border-[var(--border-color)] bg-[#050505] shadow-2xl overflow-hidden";
const panelHeaderClass =
  "flex items-center justify-between border-b border-[var(--border-color)] px-4 py-2 bg-[#0a0a0a]";
const panelBodyClass =
  "px-4 py-4 min-h-[400px] font-mono text-xs overflow-auto max-h-[600px]";

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "--:--:--";
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export function BuildLogsViewer({ deploy }: BuildLogsViewerProps) {
  const logs = useMemo(() => {
    return (deploy.cicdLogs ?? []).map((log, index) => ({
      id: `build-${index}-${log.createdAt}`,
      timestamp: log.createdAt,
      level: (log.level ?? "info").toLowerCase(),
      message: log.message,
      stackTrace: log.stackTrace,
    }));
  }, [deploy.cicdLogs]);

  const lastTimestamp = logs.at(-1)?.timestamp;

  return (
    <div className="space-y-6">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <div className="flex items-center gap-4">
            {/* Window Controls */}
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f56] opacity-80 hover:opacity-100 transition-opacity"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e] opacity-80 hover:opacity-100 transition-opacity"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[#27c93f] opacity-80 hover:opacity-100 transition-opacity"></div>
            </div>

            {/* Title */}
            <div className="flex items-center gap-2 text-[var(--text-subtle)] text-xs font-mono opacity-80 pl-2 border-l border-[var(--border-color)]">
              <Terminal className="w-3 h-3" />
              <span className="uppercase tracking-wider">build_logs.log</span>
            </div>
          </div>

          {/* Technical Metadata Block */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] uppercase tracking-wider font-medium text-[var(--text-subtle)] opacity-50">
                LNS:
              </span>
              <span className="text-xs font-mono text-[var(--text-subtle)]">
                {logs.length}
              </span>
            </div>

            {lastTimestamp && (
              <div className="flex items-center gap-2 pl-3 border-l border-[var(--border-color)]">
                <span className="text-[9px] uppercase tracking-wider font-medium text-[var(--text-subtle)] opacity-50">
                  T_STAMP:
                </span>
                <span className="text-xs font-mono text-[var(--text-subtle)]">
                  {formatTime(lastTimestamp)}
                </span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className={panelBodyClass}>
          <BuildLogsList deploy={deploy} />
        </CardContent>
      </Card>
    </div>
  );
}
